import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './authConfig';
import React from 'react';
import { initializeIcons } from '@fluentui/react';
import { initI18n } from './i18n';

export const msalInstance = new PublicClientApplication(msalConfig);

// React tree is the single owner of anything that needs to react to
// account changes (label loading, routing, rendering). index.tsx only
// boots MSAL, boots default i18n, and hands off to React. No side
// effects fired from MSAL event callbacks, no window.location.reload:
// reload was a workaround that made MSAL's redirect flow work with an
// old imperative auth check, but caused a visible double-render after
// login. MsalProvider already re-renders the tree on LOGIN_SUCCESS, and
// useAccessStatus picks up the account change on that re-render.
msalInstance.addEventCallback((event: any) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
    msalInstance.setActiveAccount(event.payload.account);
  }
  if (event.eventType === EventType.LOGIN_FAILURE) {
    console.error('Login failed:', event);
  }
});

initializeIcons();

(async () => {
  // CRITICAL: Must initialize MSAL before any API calls
  await msalInstance.initialize();

  // Only auto-activate when there's exactly one cached account. Multiple
  // accounts typically means a previous sign-out left residue; picking
  // one blindly (the old accounts[0]) causes the "wrong identity sent
  // to backend" bug. In the ambiguous case we leave activeAccount unset
  // and let App render the "no access" branch with a Sign Out button.
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 1) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  // Default labels must exist before first render so no component ever
  // sees raw translation keys. User-specific labels are loaded inside
  // React (see useUserLabels) once an active account exists.
  await initI18n('clientX');

  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  );
  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <App instance={msalInstance} />
      </MsalProvider>
    </React.StrictMode>
  );
})();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
